import React, { useState, useEffect } from "react";
import {
  FiMessageSquare, FiInstagram, FiPhone, FiMapPin,
  FiCheckCircle, FiX, FiSend, FiArrowRight, FiArrowLeft,
} from "react-icons/fi";
import "./Contact.css";
import useLoading from "../Hooks/useLoading";

const questions = [
  {
    name: "objetivo",
    number: "01",
    title: "Qual seu objetivo principal?",
    hint: "Ex: hipertrofia, emagrecimento, performance, saúde.",
    placeholder: "Escreve aqui seu objetivo com a consultoria...",
    optional: false,
  },
  {
    name: "lesao",
    number: "02",
    title: "Alguma restrição ou lesão?",
    hint: "Se não tiver, pode deixar em branco.",
    placeholder: "Lesões prévias ou atuais, limitações de movimento...",
    optional: true,
  },
  {
    name: "tempo_por_dia",
    number: "03",
    title: "Quanto tempo por dia pra treinar?",
    hint: "Ex: 1h, 1h30, 2h por sessão.",
    placeholder: "Tempo médio que você consegue dedicar...",
    optional: false,
  },
];

const WHATSAPP_NUMBER = "5561995909917";
const WHATSAPP_MESSAGE = "Olá! Gostaria de saber mais sobre a consultoria.";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    idade: "",
    altura: "",
    peso: "",
    objetivo: "",
    lesao: "",
    tempo_por_dia: "",
  });

  const [phase, setPhase] = useState("personal"); // "personal" | "questions" | "done"
  const [questionIndex, setQuestionIndex] = useState(0);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { wrap } = useLoading();
  const currentQuestion = questions[questionIndex];

  // Lock scroll enquanto modal aberto
  useEffect(() => {
    document.body.style.overflow = phase === "questions" ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [phase]);

  // Fechar com ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && phase === "questions") closeModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 4000);
  };

  const openModal = (e) => {
    e.preventDefault();
    setQuestionIndex(0);
    setPhase("questions");
  };

  const closeModal = () => {
    setPhase("personal");
    setQuestionIndex(0);
  };

  const handleQuestionNext = () => {
    if (!currentQuestion.optional && !formData[currentQuestion.name].trim()) {
      return;
    }
    if (questionIndex < questions.length - 1) {
      setQuestionIndex(questionIndex + 1);
    } else {
      handleSubmit();
    }
  };

  const handleQuestionBack = () => {
    if (questionIndex > 0) {
      setQuestionIndex(questionIndex - 1);
    } else {
      closeModal();
    }
  };

  const handleWhatsAppClick = () => {
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;
    window.open(url, "_blank");
  };

  const handleSubmit = async () => {
  setIsSubmitting(true);

  try {
    const body = new FormData();
    Object.entries(formData).forEach(([k, v]) => body.append(k, v ?? ""));

    const response = await wrap(
      fetch("https://formspree.io/f/meaapnyb", {
        method: "POST",
        body,
        headers: { Accept: "application/json" },
      })
    );

    if (!response.ok) throw new Error("Erro ao enviar");

    setPhase("done");
    showToast("✅ Anamnese enviada! Entraremos em contato por email.", "success");

    setTimeout(() => {
      setFormData({
        name: "", email: "", phone: "", idade: "", altura: "", peso: "",
        objetivo: "", lesao: "", tempo_por_dia: "",
      });
      setPhase("personal");
      setQuestionIndex(0);
      setIsSubmitting(false);
    }, 2500);
  } catch (error) {
    console.error(error);
    showToast("❌ Erro ao enviar. Tente novamente ou use o WhatsApp.", "error");
    setIsSubmitting(false);
  }
};

  return (
    <section className="contact" id="contact" data-animate="fade-right" data-delay="200">
      <div className="contact-container">

        <header className="contact-header">
          <h2 className="contact-title">Pronto para transformar seu físico?</h2>
          <p className="contact-subtitle">
            Preencha a anamnese para uma avaliação completa e personalizada.
          </p>
        </header>

        <div className="contact-content">

          <aside className="contact-info">
            <div className="contact-info-item">
              <div className="contact-info-icon"><FiPhone /></div>
              <div className="contact-info-text">
                <h3>Telefone</h3>
                <p>+55 (61) 9 9590-9917</p>
              </div>
            </div>

            <div className="contact-info-item">
              <div className="contact-info-icon"><FiInstagram /></div>
              <div className="contact-info-text">
                <h3>Instagram</h3>
                <p>@luissaboia.ofc</p>
              </div>
            </div>

            <div className="contact-info-item">
              <div className="contact-info-icon"><FiMapPin /></div>
              <div className="contact-info-text">
                <h3>Localização</h3>
                <p>Brasília, Brasil</p>
              </div>
            </div>

            <div className="contact-info-whatsapp">
              <button
                type="button"
                className="contact-whatsapp-btn"
                onClick={handleWhatsAppClick}
              >
                <FiMessageSquare />
                Conversar no WhatsApp
              </button>
              <p className="contact-info-hint">Resposta em minutos · sem formulário</p>
            </div>
          </aside>

          <form className="contact-form" onSubmit={openModal}>

            <div className="contact-form-intro">
              <span className="contact-form-intro-label">Ou preencha a anamnese</span>
              <p className="contact-form-intro-text">
                3 perguntas rápidas. Enviamos por email e entramos em contato.
              </p>
            </div>

            <input
              className="contact-input"
              type="text"
              name="name"
              placeholder="Nome completo"
              value={formData.name}
              onChange={handleInputChange}
              required
            />

            <div className="contact-form-row">
              <input
                className="contact-input"
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
              <input
                className="contact-input"
                type="tel"
                name="phone"
                placeholder="Telefone"
                value={formData.phone}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="contact-form-row contact-form-row--three">
              <input
                className="contact-input"
                type="text"
                name="idade"
                placeholder="Idade"
                value={formData.idade}
                onChange={handleInputChange}
                required
              />
              <input
                className="contact-input"
                type="text"
                name="altura"
                placeholder="Altura"
                value={formData.altura}
                onChange={handleInputChange}
                required
              />
              <input
                className="contact-input"
                type="text"
                name="peso"
                placeholder="Peso"
                value={formData.peso}
                onChange={handleInputChange}
                required
              />
            </div>

            <button type="submit" className="contact-continue">
              COMEÇAR AGORA!
              <FiArrowRight />
            </button>
          </form>
        </div>
      </div>

      {/* Modal de perguntas */}
      {phase === "questions" && (
        <div className="contact-modal-overlay" onClick={closeModal}>
          <div className="contact-modal" onClick={(e) => e.stopPropagation()}>

            <button
              type="button"
              className="contact-modal-close"
              onClick={closeModal}
              aria-label="Fechar"
            >
              <FiX />
            </button>

            <div className="contact-modal-progress">
              <span className="contact-modal-progress-label">
                {questionIndex + 1} de {questions.length}
              </span>
              <div className="contact-modal-progress-track">
                <div
                  className="contact-modal-progress-fill"
                  style={{ width: `${((questionIndex + 1) / questions.length) * 100}%` }}
                />
              </div>
            </div>

            <div className="contact-modal-body" key={currentQuestion.name}>
              <span className="contact-modal-number">{currentQuestion.number}</span>
              <h3 className="contact-modal-title">{currentQuestion.title}</h3>
              <p className="contact-modal-hint">{currentQuestion.hint}</p>

              <textarea
                className="contact-modal-textarea"
                name={currentQuestion.name}
                placeholder={currentQuestion.placeholder}
                rows="3"
                value={formData[currentQuestion.name]}
                onChange={handleInputChange}
                autoFocus
              />
            </div>

            <div className="contact-modal-actions">
              <button
                type="button"
                className="contact-modal-back"
                onClick={handleQuestionBack}
              >
                <FiArrowLeft />
                {questionIndex === 0 ? "Voltar" : "Anterior"}
              </button>

              <button
                type="button"
                className="contact-modal-next"
                onClick={handleQuestionNext}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Enviando..."
                  : questionIndex === questions.length - 1
                  ? "Enviar"
                  : "Próxima"}
                {!isSubmitting && questionIndex === questions.length - 1
                  ? <FiSend />
                  : <FiArrowRight />}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast.show && (
        <div className={`contact-toast contact-toast--${toast.type}`}>
          <div className="contact-toast-content">
            <FiCheckCircle className="contact-toast-icon" />
            <span className="contact-toast-message">{toast.message}</span>
            <button
              type="button"
              className="contact-toast-close"
              onClick={() => setToast({ show: false, message: "", type: "" })}
              aria-label="Fechar"
            >
              <FiX />
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default Contact;